// How each question type is graded (12b). Manually/AI-graded types are free-text
// and need a human or AI verdict; every other type is deterministically
// auto-graded. Mirrors the question schema's rubric comment
// ("manually graded types: OPEN_ENDED / SHORT_ANSWER").
export const MANUAL_QUESTION_TYPES = new Set<string>(['OPEN_ENDED', 'SHORT_ANSWER']);

export const isManuallyGraded = (input: string | QuestionLike): boolean => {
  const question = typeof input === 'string' ? { type: input } : input;
  if (question.type === 'SHORT_ANSWER') return question.answerMode !== 'exact';
  return question.type === 'OPEN_ENDED' || (question.type === 'PASSAGE' && Boolean(question.subQuestions?.some(isManuallyGraded)));
};

// i18n key for each canonical question type (labels already exist in locales).
export const QUESTION_TYPE_LABEL_KEY: Record<string, string> = {
  MULTIPLE_CHOICE: 'multiple_choice',
  MULTIPLE_RESPONSE: 'multiple_response',
  TRUE_FALSE: 'true_false',
  SHORT_ANSWER: 'short_answer',
  MATCHING: 'matching',
  FILL_IN_BLANKS: 'fill_in_blanks',
  OPEN_ENDED: 'open_ended',
  REORDER: 'reorder',
  PASSAGE: 'passage',
};

export interface TypeTally {
  type: string;
  count: number;
  points: number;
  manual: boolean;
}

interface QuestionLike {
  type?: string;
  answerMode?: 'exact' | 'manual';
  subQuestions?: QuestionLike[];
  points?: number;
}
interface SectionLike {
  questions?: QuestionLike[];
}

/** Tally the test's questions by type, summing points and marking each Auto vs Manual. */
export function tallyQuestionTypes(sections?: SectionLike[], rootQuestions?: QuestionLike[]): TypeTally[] {
  const tallies = new Map<string, TypeTally>();
  const collect = (questions?: QuestionLike[]) => (questions ?? []).forEach((q) => {
    const type = String(q.type || '').toUpperCase();
    if (!type) return;
    const manual = isManuallyGraded(q), key = `${type}:${manual}`;
    const tally = tallies.get(key) ?? { type, count: 0, points: 0, manual };
    tally.count++; tally.points += q.points ?? 1; tallies.set(key, tally);
  });
  if (sections?.length) sections.forEach((section) => collect(section.questions)); else collect(rootQuestions);
  return [...tallies.values()].sort((a, b) => b.count - a.count);
}

// Presentation buckets for the by-type summary (12b): choice, fill/true-false,
// open-ended (manual), and a catch-all for any other auto-graded type present.
export interface GroupTally {
  key: string;
  labelKey: string;
  count: number;
  points: number;
  manual: boolean;
}

const GROUP_FOR_TYPE: Record<string, { key: string; labelKey: string; manual: boolean }> = {
  MULTIPLE_CHOICE: { key: 'choice', labelKey: 'grading_group_choice', manual: false },
  MULTIPLE_RESPONSE: { key: 'choice', labelKey: 'grading_group_choice', manual: false },
  FILL_IN_BLANKS: { key: 'fill_tf', labelKey: 'grading_group_fill_tf', manual: false },
  TRUE_FALSE: { key: 'fill_tf', labelKey: 'grading_group_fill_tf', manual: false },
  OPEN_ENDED: { key: 'open', labelKey: 'grading_group_open', manual: true },
  SHORT_ANSWER: { key: 'open', labelKey: 'grading_group_open', manual: true },
};
const OTHER_GROUP = { key: 'other', labelKey: 'grading_group_other', manual: false };
const GROUP_ORDER = ['choice', 'fill_tf', 'open', 'other'];

/** Collapse per-type tallies into the design's presentation buckets. */
export function groupTallies(tallies: TypeTally[]): GroupTally[] {
  const acc = new Map<string, GroupTally>();
  tallies.forEach((tally) => {
    const group = tally.manual ? GROUP_FOR_TYPE.OPEN_ENDED : tally.type === 'SHORT_ANSWER' ? GROUP_FOR_TYPE.FILL_IN_BLANKS : GROUP_FOR_TYPE[tally.type] ?? OTHER_GROUP;
    const existing = acc.get(group.key);
    if (existing) {
      existing.count += tally.count;
      existing.points += tally.points;
    } else {
      acc.set(group.key, {
        key: group.key,
        labelKey: group.labelKey,
        count: tally.count,
        points: tally.points,
        manual: group.manual,
      });
    }
  });
  return GROUP_ORDER.filter((key) => acc.has(key)).map((key) => acc.get(key)!);
}

/** Totals for the page subtitle: how many questions auto-grade vs need manual review. */
export function summarizeGrading(tallies: TypeTally[]): { total: number; auto: number; manual: number } {
  return tallies.reduce(
    (sum, tally) => ({
      total: sum.total + tally.count,
      auto: sum.auto + (tally.manual ? 0 : tally.count),
      manual: sum.manual + (tally.manual ? tally.count : 0),
    }),
    { total: 0, auto: 0, manual: 0 },
  );
}
